import { apiUrl } from '@/api/client';
import { resolveWidgetMediaUrl } from '@/lib/resolveWidgetMediaUrl';

export type PwaBookingChangelogEntry = {
  action: string;
  timestamp: string;
  operatorId?: string;
  reason?: string;
  changes?: Record<string, { from: unknown; to: unknown }>;
};

export type PwaBookingDetail = {
  id: string;
  resourceId: string;
  virtualAgentId: string;
  conversationId: string | null;
  sourceMessageId?: string | null;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
  startDate: string;
  endDate: string;
  timeBlock: string | null;
  status: string;
  depositReceiptUrl: string | null;
  publicReference: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  cancellationReason: string | null;
  createdBy: string;
  changelog: PwaBookingChangelogEntry[];
  createdAt: string;
  updatedAt: string;
  /** Fin de la hora de seña. Null si esta reserva no vence sola. */
  depositExpiresAt?: string | null;
  resource?: {
    id: string;
    name: string;
    resourceType?: {
      id: string;
      name: string;
      bookingMode: string;
      bookingStatusSettings?: unknown;
      category?: string;
      checkInTime?: string | null;
      checkOutTime?: string | null;
    };
  };
};

const STATUS_LABELS: Record<string, string> = {
  pending_deposit: 'Pendiente de seña',
  confirmed: 'Confirmada',
  human_supervision: 'Supervisión humana',
  cancelled: 'Cancelada',
  auto_cancelled: 'Cancelada automáticamente',
  completed: 'Completada (hist.)',
};

export function bookingStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/** Tipo de recurso exige comprobante antes de confirmar (config en consola). */
export function bookingRequiresDepositBeforeConfirm(detail: PwaBookingDetail): boolean {
  const raw = detail.resource?.resourceType?.bookingStatusSettings;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  return (raw as { requireDepositReceiptBeforeConfirm?: boolean }).requireDepositReceiptBeforeConfirm === true;
}

export function formatBookingDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatBookingDateShort(iso: string): string {
  return new Date(iso).toLocaleString('es', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function changelogActionLabel(action: string): string {
  switch (action) {
    case 'created':
      return 'Creada';
    case 'modified':
      return 'Modificada';
    case 'cancelled':
      return 'Cancelada';
    case 'auto_cancelled':
      return 'Auto-cancelada';
    case 'confirmed':
      return 'Confirmada';
    case 'receipt_attached':
      return 'Comprobante adjunto';
    default:
      return action;
  }
}

export function resolveDepositReceiptHref(raw: string): string {
  const t = raw.trim();
  if (!t || t.startsWith('data:')) return t;
  if (t.startsWith('/widget') || /\/widget\//i.test(t)) return resolveWidgetMediaUrl(t);
  if (t.startsWith('http://') || t.startsWith('https://')) return resolveWidgetMediaUrl(t);
  return apiUrl(t.startsWith('/') ? t : `/${t}`);
}

export function depositReceiptIsImage(raw: string): boolean {
  const t = raw.trim();
  if (t.startsWith('data:image/')) return true;
  if (t.startsWith('data:')) return false;
  try {
    const pathOnly = /^https?:\/\//i.test(t)
      ? new URL(t).pathname
      : (t.split('?')[0]?.split('#')[0] ?? t);
    return /\.(jpe?g|png|gif|webp)$/i.test(pathOnly);
  } catch {
    return /\.(jpe?g|png|gif|webp)(\?|$)/i.test(t);
  }
}

export function depositReceiptIsPdf(raw: string): boolean {
  const t = raw.trim();
  if (/^data:application\/pdf/i.test(t)) return true;
  try {
    const pathOnly = /^https?:\/\//i.test(t)
      ? new URL(t).pathname
      : (t.split('?')[0]?.split('#')[0] ?? t);
    return /\.pdf$/i.test(pathOnly);
  } catch {
    return /\.pdf(\?|$)/i.test(t);
  }
}

export function depositReceiptDownloadName(raw: string): string {
  const m = raw.match(/^data:([^;,]+)/i);
  const mime = (m?.[1] ?? '').toLowerCase();
  if (mime.includes('pdf')) return 'comprobante-seña.pdf';
  if (mime.includes('png')) return 'comprobante-seña.png';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'comprobante-seña.jpg';
  if (mime.includes('gif')) return 'comprobante-seña.gif';
  if (mime.includes('webp')) return 'comprobante-seña.webp';
  return 'comprobante-seña';
}
